import React from 'react';
import { Card, Table, Typography, Alert, Tabs, Badge } from 'antd';
import { ExclamationCircleOutlined, DatabaseOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Text } = Typography;
const { TabPane } = Tabs;

export interface FilteredItem {
  materialNumber: string;
  articleName: string;
  reason: string;
}

export interface FilteredItemsData {
  zeroTimeItems: FilteredItem[];
  notInMrpItems: FilteredItem[];
}

interface FilteredItemsTableProps {
  filteredItems: FilteredItemsData;
  visible: boolean;
}

const FilteredItemsTable: React.FC<FilteredItemsTableProps> = ({ 
  filteredItems, 
  visible 
}) => {
  if (!visible || (!filteredItems?.zeroTimeItems?.length && !filteredItems?.notInMrpItems?.length)) {
    return null;
  }

  const columns: ColumnsType<FilteredItem> = [
    {
      title: 'Materialnummer',
      dataIndex: 'materialNumber',
      key: 'materialNumber',
      width: '30%',
      sorter: (a, b) => a.materialNumber.localeCompare(b.materialNumber),
      render: (text: string) => (
        <Text strong copyable={{ text }}>{text}</Text>
      ),
    },
    {
      title: 'Artikel',
      dataIndex: 'articleName',
      key: 'articleName',
      width: '70%',
      sorter: (a, b) => a.articleName.localeCompare(b.articleName),
      render: (text: string) => (
        <Text copyable={{ text }}>{text}</Text>
      ),
    },
  ];

  const totalFilteredCount = filteredItems.zeroTimeItems.length + filteredItems.notInMrpItems.length;

  return (
    <Card 
      title={
        <span>
          <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
          Ausgeschlossene Artikel
          <Badge 
            count={totalFilteredCount} 
            style={{ backgroundColor: '#faad14', marginLeft: 8 }}
          />
        </span>
      }
      style={{ marginTop: 24 }}
      size="small"
    >
      <Alert
        message="Information"
        description="Diese Artikel werden nicht im Gantt-Diagramm angezeigt, da sie entweder keine Zeiten haben oder nicht in den MRP-Daten gefunden wurden."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <Tabs defaultActiveKey="zeroTime">
        <TabPane
          tab={
            <span>
              <ExclamationCircleOutlined />
              Artikel ohne Zeit
              <Badge 
                count={filteredItems.zeroTimeItems.length} 
                style={{ backgroundColor: '#ff4d4f', marginLeft: 8 }}
              />
            </span>
          }
          key="zeroTime"
        >
          {filteredItems.zeroTimeItems.length > 0 ? (
            <>
              <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
                Diese Artikel haben keine Eigenfertigungs- oder Planlieferzeit in den MRP-Daten:
              </Text>
              <Table
                columns={columns}
                dataSource={filteredItems.zeroTimeItems.map((item, index) => ({
                  ...item,
                  key: `zero-${index}`,
                }))}
                size="small"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Gesamt ${total} Artikel`,
                }}
                scroll={{ x: true }}
              />
            </>
          ) : (
            <Alert
              message="Keine Artikel ohne Zeit"
              description="Alle Artikel haben gültige Zeit-Werte."
              type="success"
              showIcon
            />
          )}
        </TabPane>

        <TabPane
          tab={
            <span>
              <DatabaseOutlined />
              Nicht in MRP
              <Badge 
                count={filteredItems.notInMrpItems.length} 
                style={{ backgroundColor: '#ff4d4f', marginLeft: 8 }}
              />
            </span>
          }
          key="notInMrp"
        >
          {filteredItems.notInMrpItems.length > 0 ? (
            <>
              <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
                Diese Artikel wurden in der Stückliste gefunden, aber nicht in den MRP-Daten für das ausgewählte Werk:
              </Text>
              <Table
                columns={columns}
                dataSource={filteredItems.notInMrpItems.map((item, index) => ({
                  ...item,
                  key: `mrp-${index}`,
                }))}
                size="small"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Gesamt ${total} Artikel`,
                }}
                scroll={{ x: true }}
              />
            </>
          ) : (
            <Alert
              message="Alle Artikel in MRP gefunden"
              description="Alle Artikel aus der Stückliste wurden in den MRP-Daten gefunden."
              type="success"
              showIcon
            />
          )}
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default FilteredItemsTable;